'use client';
import { useEffect, useState } from 'react';

// Delays mounting non-critical third-party widgets until a real user interacts,
// OR a minimum delay has passed with no interaction.
//
// Do NOT use requestIdleCallback as the primary trigger — it fires as soon as the
// browser sees any lull (often within 1-3s), which doesn't remove the third-party
// script's blocking time from Lighthouse's Total Blocking Time window, it just
// relocates it there. A plain minimum-delay timer, calibrated to fire after the
// page's own first-party JS would naturally settle, keeps that cost out of the
// TBT window in the common (no-interaction) case Lighthouse measures.
export default function useDeferredMount({ minDelay = 5000 } = {}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let settled = false;
    const markReady = () => {
      if (settled) return;
      settled = true;
      setReady(true);
    };

    const interactionEvents = ['scroll', 'pointerdown', 'keydown', 'touchstart'];
    interactionEvents.forEach((evt) =>
      window.addEventListener(evt, markReady, { passive: true, once: true })
    );
    const delayId = window.setTimeout(markReady, minDelay);

    return () => {
      window.clearTimeout(delayId);
      interactionEvents.forEach((evt) => window.removeEventListener(evt, markReady));
    };
  }, [minDelay]);

  return ready;
}
